// Ported verbatim from Dealtracker src/appframe/tours/useTourState.ts (commit 30ca0cb).
// Niobe screens publish booleans and counts only, never bid content (docs/spec/62-help.md).
import { useEffect } from 'react';

/**
 * Publish/subscribe registry of screen state for tour probes (D3). Screens publish what
 * a probe needs (`useTourState('dealList', { useCase, filters, presets })`), so probes
 * never reach into component internals. Read-only for the engine: nothing here writes
 * back to a screen.
 */
const states = new Map<string, unknown>();
const listeners = new Set<() => void>();

function notify() {
  for (const l of [...listeners]) l();
}

export function publishTourState(key: string, value: unknown): void {
  if (states.has(key) && states.get(key) === value) return;
  states.set(key, value);
  notify();
}

export function clearTourState(key: string): void {
  if (!states.has(key)) return;
  states.delete(key);
  notify();
}

export function getTourState<T = unknown>(key: string): T | undefined {
  return states.get(key) as T | undefined;
}

/** Calls `listener` after every publish/clear. Returns the unsubscribe function. */
export function subscribeTourState(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** Test helper: drops every published state and listener. */
export function resetTourState(): void {
  states.clear();
  listeners.clear();
}

/**
 * Publishes `value` under `key` while the calling component is mounted. Pass a memoised
 * value: a new object on every render publishes (and re-evaluates probes) every render.
 */
export function useTourState(key: string, value: unknown): void {
  useEffect(() => { publishTourState(key, value); }, [key, value]);
  useEffect(() => () => clearTourState(key), [key]);
}
